import React from "react";

import type { TelemetryBudgetMeta, TelemetryQueryMeta } from "@/lib/telemetry";
import { formatBytes, formatDurationMs, formatUsd } from "@/components/telemetry/format";

const CACHE_LABEL: Record<string, string> = {
  miss: "scanned",
  bundle: "cached window",
  result_reuse: "Athena result reuse",
};

/**
 * What this screen cost. Small, but never hidden.
 *
 * Shown for cache hits too: a report that only counts the misses cannot answer
 * "why did the bill grow", and the 10 MB per-query floor means the cheap-looking
 * frequent queries are the ones that add up.
 */
export function QueryCost({
  queries,
  budget,
  mode,
  truncated,
}: {
  queries: TelemetryQueryMeta[];
  budget?: TelemetryBudgetMeta;
  mode?: string;
  truncated?: boolean;
}) {
  const scans = queries.filter(Boolean);
  if (!scans.length) return null;

  const bytes = scans.reduce((sum, q) => sum + (q.bytes_scanned || 0), 0);
  const cost = scans.reduce((sum, q) => sum + (q.estimated_cost_usd || 0), 0);
  const slowest = Math.max(...scans.map((q) => q.duration_ms || 0));
  const caches = Array.from(new Set(scans.map((q) => CACHE_LABEL[q.cache] ?? q.cache)));
  const queryIds = scans.map((q) => q.athena_query_id).filter(Boolean) as string[];

  return (
    <div
      data-testid="telemetry-query-cost"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground"
    >
      <span>
        {scans.length > 1 ? `${scans.length} scans · ` : ""}
        <span className="font-mono">{formatBytes(bytes)}</span> scanned
      </span>
      <span>
        est. <span className="font-mono">{formatUsd(cost)}</span>
      </span>
      <span>{caches.join(" + ")}</span>
      <span>{formatDurationMs(slowest)}</span>
      {mode && <span>mode: {mode}</span>}
      {truncated && <span className="text-amber-600 dark:text-amber-400">result truncated</span>}
      {budget && (
        <span>
          budget {formatBytes(budget.spent_bytes_today)} / {formatBytes(budget.limit_bytes)} today
        </span>
      )}
      {/* Keyed by position: two scans of the same window legitimately report the
          same query id when Athena reuses a previous result. */}
      {queryIds.map((id, index) => (
        <span key={`${id}-${index}`} className="font-mono opacity-70" title="Athena query id">
          {id.slice(0, 8)}…
        </span>
      ))}
    </div>
  );
}
